import React from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Calendar, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EntityActions from "@/components/shared/EntityActions";
import { getStatusBadge } from "@/components/projects/ProjectStatusDropdown";
import { getCustomerDisplayName } from "@/utils/customerDisplayHelpers";
import type { Project } from "@/pages/projects/hooks/useProjectData";

interface ProjectsTableProps {
  projects: Project[];
  getProjectActions: (project: Project) => any[];
}

/**
 * ProjectsTable - Table view for projects list
 * 
 * Displays projects in a tabular format with columns for:
 * - Project name (linked to detail page)
 * - Customer
 * - Status badge
 * - Start/End dates
 * - Actions menu
 */
const ProjectsTable: React.FC<ProjectsTableProps> = ({ projects, getProjectActions }) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Project Name</TableHead>
          <TableHead className="hidden md:table-cell">Customer</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="hidden lg:table-cell">Start Date</TableHead>
          <TableHead className="hidden lg:table-cell">End Date</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {projects.map((project) => (
          <TableRow key={project.id}>
            <TableCell>
              <Link
                to={`/projects/${project.id}`}
                className="font-medium hover:underline"
              >
                {project.name}
              </Link>
              {project.description && (
                <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                  {project.description}
                </p>
              )}
            </TableCell>
            <TableCell className="hidden md:table-cell">
              {project.customers ? getCustomerDisplayName(project.customers) : (
                <span className="text-muted-foreground">No customer</span>
              )}
            </TableCell>
            <TableCell>
              {getStatusBadge(project.status)}
            </TableCell>
            <TableCell className="hidden lg:table-cell">
              {project.expected_start_date ? (
                <div className="flex items-center gap-1 text-sm">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(project.expected_start_date), 'dd MMMM yyyy')}
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">Not set</span>
              )}
            </TableCell>
            <TableCell className="hidden lg:table-cell">
              {project.expected_end_date ? (
                <div className="flex items-center gap-1 text-sm">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(project.expected_end_date), 'dd MMMM yyyy')}
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">Not set</span>
              )}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex items-center gap-1 justify-end">
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/projects/${project.id}`}>
                    <Eye className="h-4 w-4" />
                  </Link>
                </Button>
                <EntityActions actions={getProjectActions(project)} size="sm" />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ProjectsTable;
